import React, { useEffect, useState, useCallback } from "react";
import { Layers, Eye, ShieldCheck, Server, Loader2, Info, Tag, Plus, Trash2, Sparkles, ShieldAlert, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../App";
import { ActionBadge, ACTION_META, Card, Pill, Section, Btn } from "./ui";

const ACTIONS = ["ALLOW", "WARN", "MASK", "BLOCK"];
const SEVERITY = Object.fromEntries(ACTIONS.map((a, i) => [a, i]));

function decidingLayer(r) {
  // A clamped (looser-than-floor) override means the Sector floor actually decided.
  if (r.sector_floor_enforced) return "Sector floor";
  if (r.department_override && r.department_override !== r.company_override) return "Department";
  if (r.company_override) return "Company";
  return "Sector";
}

function LayerRow({ label, action, inherited, indent, active }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-md py-0.5 ${active ? "bg-brand/10 ring-1 ring-brand/30" : ""}`}
      style={{ marginLeft: indent * 16, paddingLeft: 6, paddingRight: 6 }}
    >
      {indent > 0 && <span className="font-mono text-slate-700">└</span>}
      <span className={`w-20 font-mono text-[9px] uppercase tracking-wider ${active ? "text-brand" : "text-slate-500"}`}>{label}</span>
      {inherited ? (
        <span className="font-mono text-[10px] italic text-slate-600">inherits</span>
      ) : (
        <ActionBadge action={action} small />
      )}
    </div>
  );
}

function LayerCascade({ r, saving }) {
  const by = decidingLayer(r);
  return (
    <div className="space-y-1 rounded-lg border border-subtle bg-elevated/40 p-2.5" data-testid={`cascade-${r.category}`}>
      <LayerRow label="Sector floor" action={r.sector_default} indent={0} active={by === "Sector" || by === "Sector floor"} />
      <LayerRow label="Company" action={r.company_override} inherited={!r.company_override} indent={1} active={by === "Company"} />
      <LayerRow label="Department" action={r.department_override} inherited={!r.department_override} indent={2} active={by === "Department"} />
      <div className="mt-1 flex items-center gap-2 border-t border-subtle pt-1.5">
        <span className="font-mono text-slate-600">⇒</span>
        <span className="w-20 font-mono text-[9px] uppercase tracking-wider text-slate-500">Effective</span>
        {saving ? (
          <Loader2 className="h-4 w-4 animate-spin text-brand" />
        ) : (
          <ActionBadge action={r.effective_action} small testid={`effective-${r.category}`} />
        )}
        <span className="rounded px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-eu" style={{ background: "rgba(59,130,246,0.12)" }}>
          by {by}
        </span>
      </div>
    </div>
  );
}

function LayerChip({ label, action, inherited }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="font-mono text-[9px] uppercase text-slate-600">{label}</span>
      {inherited ? <span className="font-mono text-[10px] italic text-slate-700">·</span> : <ActionBadge action={action} small />}
    </span>
  );
}

export default function PolicyBuilder() {
  const { session } = useAuth();
  const tid = session.tenant.id;
  const [scope, setScope] = useState("company");
  const [deptId, setDeptId] = useState("");
  const [departments, setDepartments] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [providers, setProviders] = useState([]);
  const [saving, setSaving] = useState("");
  const [customCats, setCustomCats] = useState([]);
  const [ccForm, setCcForm] = useState({ label: "", desc: "", pattern: "", keywords: "", default_action: "MASK" });
  const [ccErr, setCcErr] = useState("");
  const [ccBusy, setCcBusy] = useState(false);
  const [ccSample, setCcSample] = useState("");
  const [ccMatches, setCcMatches] = useState(null);
  const [ccTestBusy, setCcTestBusy] = useState(false);
  const [presetBusy, setPresetBusy] = useState("");
  const [configOpen, setConfigOpen] = useState(false);
  const [blockSel, setBlockSel] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [events, setEvents] = useState([]);
  const [pendingPreset, setPendingPreset] = useState(null);

  const loadCustom = useCallback(() => {
    api.customCategories(tid).then(setCustomCats);
  }, [tid]);

  const loadEvents = useCallback(() => {
    api.policyEvents(tid).then(setEvents).catch(() => {});
  }, [tid]);

  useEffect(() => {
    api.departments(tid).then(setDepartments);
    api.providers().then(setProviders);
    loadCustom();
    loadEvents();
  }, [tid, loadCustom, loadEvents]);

  const load = useCallback(() => {
    const d = scope === "department" ? deptId : undefined;
    api.policy(tid, d).then(setPolicy);
  }, [tid, scope, deptId]);

  useEffect(() => {
    if (scope === "department" && !deptId && departments[0]) {
      setDeptId(departments[0].id);
      return;
    }
    load();
  }, [scope, deptId, departments, load]);

  const setAction = async (category, action) => {
    setSaving(category);
    const updated = await api.updatePolicy(tid, {
      scope,
      department_id: scope === "department" ? deptId : null,
      category,
      action,
    });
    setPolicy(updated);
    setSaving("");
  };

  const toggleMonitor = async (rule) => {
    setSaving(rule.category);
    const updated = await api.updatePolicy(tid, {
      scope,
      department_id: scope === "department" ? deptId : null,
      category: rule.category,
      monitor: !rule.monitor,
    });
    setPolicy(updated);
    setSaving("");
  };

  useEffect(() => {
    if (policy && Array.isArray(policy.block_preset_categories)) {
      setBlockSel(policy.block_preset_categories);
    }
  }, [policy]);

  const applyPreset = async (preset) => {
    setPresetBusy(preset);
    try {
      const updated = await api.setPolicyPreset(tid, { preset, user_id: session.user.id });
      setPolicy(updated);
      loadEvents();
      setPendingPreset(null);
    } finally {
      setPresetBusy("");
    }
  };

  const presetDiff = (preset) => {
    if (!policy) return [];
    const targetFor = (r) => {
      if (preset === "tokenize_all") return r.sector_default;
      return blockSel.includes(r.category) ? "BLOCK" : r.sector_default;
    };
    return policy.rules
      .map((r) => ({ label: r.label, from: r.effective_action, to: targetFor(r) }))
      .filter((x) => x.from !== x.to);
  };

  const toggleBlockCat = (cat) => {
    setBlockSel((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));
  };

  const saveBlockConfig = async () => {
    setSavingConfig(true);
    try {
      const updated = await api.setPresetConfig(tid, blockSel, session.user.id);
      setPolicy(updated);
      loadEvents();
      setConfigOpen(false);
    } finally {
      setSavingConfig(false);
    }
  };

  const addCustomCat = async () => {
    setCcErr("");
    if (!ccForm.label.trim()) { setCcErr("Name is required"); return; }
    if (!ccForm.pattern.trim() && !ccForm.keywords.trim()) {
      setCcErr("Provide a regex pattern and/or keywords"); return;
    }
    setCcBusy(true);
    try {
      await api.addCustomCategory(tid, {
        label: ccForm.label,
        desc: ccForm.desc,
        pattern: ccForm.pattern.trim() || null,
        keywords: ccForm.keywords.split(",").map((k) => k.trim()).filter(Boolean),
        default_action: ccForm.default_action,
      });
      setCcForm({ label: "", desc: "", pattern: "", keywords: "", default_action: "MASK" });
      loadCustom();
      load();
    } catch (e) {
      setCcErr(e?.response?.data?.detail || "Could not create category");
    } finally {
      setCcBusy(false);
    }
  };

  const delCustomCat = async (cid) => {
    await api.delCustomCategory(cid);
    loadCustom();
    load();
  };

  const testPattern = async () => {
    if (!ccSample.trim()) return;
    setCcTestBusy(true);
    setCcMatches(null);
    try {
      const res = await api.testPattern({
        pattern: ccForm.pattern.trim() || null,
        keywords: ccForm.keywords.split(",").map((k) => k.trim()).filter(Boolean),
        sample_text: ccSample,
      });
      setCcMatches(res);
    } catch (e) {
      setCcMatches({ error: e?.response?.data?.detail || "Test failed", matches: [] });
    } finally {
      setCcTestBusy(false);
    }
  };

  const currentAllow =
    policy && (scope === "department" ? policy.department_allowlist || policy.company_allowlist : policy.company_allowlist);

  const toggleProvider = async (pid) => {
    const set = new Set(currentAllow || []);
    if (set.has(pid)) set.delete(pid);
    else set.add(pid);
    await api.updateAllowlist(tid, {
      scope,
      department_id: scope === "department" ? deptId : null,
      providers: Array.from(set),
    });
    load();
  };

  return (
    <Section
      title="AI Policy Builder"
      subtitle="Layered rules resolve strictest-first. The Sector Pack is a floor — a layer can be stricter, never looser."
      right={
        <div className="flex items-center gap-2">
          <button
            data-testid="scope-company"
            onClick={() => setScope("company")}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${scope === "company" ? "bg-brand/15 text-brand" : "border border-subtle text-slate-400"}`}
          >
            Company Profile
          </button>
          <button
            data-testid="scope-department"
            onClick={() => setScope("department")}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${scope === "department" ? "bg-brand/15 text-brand" : "border border-subtle text-slate-400"}`}
          >
            Department Overlay
          </button>
          {scope === "department" && (
            <select
              data-testid="dept-select"
              value={deptId}
              onChange={(e) => setDeptId(e.target.value)}
              className="rounded-lg border border-subtle bg-elevated px-3 py-1.5 text-xs outline-none focus:border-brand"
            >
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          )}
        </div>
      }
    >
      {!policy ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div className="xl:col-span-2">
            {scope === "company" && (
              <Card className="mb-4 p-4" data-testid="policy-presets">
                <div className="mb-1 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-sm font-bold">Posture preset</h3>
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-slate-500">one click</span>
                </div>
                <p className="mb-3 text-[11px] text-slate-500">
                  Switch the whole company profile in one move. You can still fine-tune any category below afterwards.
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    data-testid="preset-tokenize-all"
                    onClick={() => setPendingPreset("tokenize_all")}
                    disabled={!!presetBusy}
                    className={`flex items-start gap-2.5 rounded-xl border p-3 text-left transition-colors disabled:opacity-50 ${policy.preset === "tokenize_all" ? "border-mask/60 bg-mask/10 ring-1 ring-mask/40" : "border-subtle hover:border-mask/40"}`}
                  >
                    {presetBusy === "tokenize_all" ? <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-mask" /> : <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-mask" />}
                    <span>
                      <span className="flex items-center gap-2 text-sm font-semibold text-white">
                        Tokenize everything
                        {policy.preset === "tokenize_all" && <span className="rounded-full bg-mask/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-mask">active</span>}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-slate-500">Every sensitive value is masked & forwarded to an EU provider. Nothing is blocked.</span>
                    </span>
                  </button>
                  <button
                    data-testid="preset-block-sensitive"
                    onClick={() => setPendingPreset("block_sensitive")}
                    disabled={!!presetBusy}
                    className={`flex items-start gap-2.5 rounded-xl border p-3 text-left transition-colors disabled:opacity-50 ${policy.preset === "block_sensitive" ? "border-block/60 bg-block/10 ring-1 ring-block/40" : "border-subtle hover:border-block/40"}`}
                  >
                    {presetBusy === "block_sensitive" ? <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-block" /> : <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-block" />}
                    <span>
                      <span className="flex items-center gap-2 text-sm font-semibold text-white">
                        Block the most sensitive
                        {policy.preset === "block_sensitive" && <span className="rounded-full bg-block/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-block">active</span>}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-slate-500">GDPR Art.9/10 data, credentials & legal privilege are hard-blocked. The rest is masked.</span>
                    </span>
                  </button>
                </div>

                {/* Preset diff preview — shows exactly what will change before confirming */}
                {pendingPreset && (
                  <div data-testid="preset-preview" className="mt-3 rounded-lg border border-brand/40 bg-brand/5 p-3">
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-white">
                      <Info className="h-3.5 w-3.5 text-brand" />
                      Confirm switch to “{pendingPreset === "tokenize_all" ? "Tokenize everything" : "Block the most sensitive"}”
                    </div>
                    {presetDiff(pendingPreset).length === 0 ? (
                      <p data-testid="preset-preview-empty" className="text-[11px] text-slate-500">No categories will change — this profile already matches that preset.</p>
                    ) : (
                      <>
                        <p className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                          {presetDiff(pendingPreset).length} categor{presetDiff(pendingPreset).length === 1 ? "y" : "ies"} will change
                        </p>
                        <div className="max-h-44 space-y-1 overflow-auto">
                          {presetDiff(pendingPreset).map((d) => (
                            <div key={d.label} data-testid="preset-preview-row" className="flex items-center gap-2 rounded-md border border-subtle bg-elevated/50 px-2.5 py-1.5">
                              <span className="min-w-0 flex-1 truncate text-[11px] text-slate-300">{d.label}</span>
                              <ActionBadge action={d.from} small />
                              <ArrowRight className="h-3 w-3 text-slate-600" />
                              <ActionBadge action={d.to} small />
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <Btn data-testid="preset-confirm" onClick={() => applyPreset(pendingPreset)} disabled={!!presetBusy}>
                        {presetBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Confirm switch
                      </Btn>
                      <button data-testid="preset-cancel" onClick={() => setPendingPreset(null)} className="rounded-lg border border-subtle px-3 py-1.5 text-xs text-slate-400 hover:text-white">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <div className="mt-3 border-t border-subtle pt-3">
                  <button
                    data-testid="preset-config-toggle"
                    onClick={() => setConfigOpen((v) => !v)}
                    className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-brand"
                  >
                    <ShieldAlert className="h-3.5 w-3.5" />
                    Configure “block the most sensitive” ({(policy.block_preset_categories || []).length} categories)
                  </button>
                  {configOpen && (
                    <div data-testid="preset-config-panel" className="mt-3 rounded-lg border border-subtle bg-elevated/40 p-3">
                      <p className="mb-2 text-[11px] text-slate-500">Pick exactly which categories are hard-blocked when “Block the most sensitive” is active.</p>
                      <div className="grid max-h-56 grid-cols-1 gap-1 overflow-auto sm:grid-cols-2">
                        {policy.rules.map((r) => (
                          <label key={r.category} data-testid={`block-cat-${r.category}`} className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-[11px] text-slate-300 hover:bg-white/5">
                            <input
                              type="checkbox"
                              checked={blockSel.includes(r.category)}
                              onChange={() => toggleBlockCat(r.category)}
                              className="accent-block"
                            />
                            <span className="truncate">{r.label}</span>
                          </label>
                        ))}
                      </div>
                      <Btn data-testid="preset-config-save" onClick={saveBlockConfig} disabled={savingConfig} className="mt-3">
                        {savingConfig ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Save selection
                      </Btn>
                    </div>
                  )}
                </div>

                {/* Recent posture changes (audit) */}
                {events.length > 0 && (
                  <div className="mt-3 border-t border-subtle pt-3" data-testid="policy-events">
                    <div className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">Recent posture changes</div>
                    <div className="space-y-1">
                      {events.slice(0, 4).map((e) => (
                        <div key={e.id} data-testid="policy-event" className="flex items-center gap-2 text-[11px] text-slate-400">
                          <span className="font-semibold text-slate-300">{e.user_name}</span>
                          <span className="text-slate-500">{e.detail}</span>
                          <span className="ml-auto font-mono text-[10px] text-slate-600">{new Date(e.timestamp).toLocaleString("en-GB", { hour12: false })}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}
            <PolicyDiff rules={policy.rules} scope={scope} />
            <Card className="overflow-hidden">
              <div className="grid grid-cols-12 gap-3 border-b border-subtle bg-elevated/50 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <div className="col-span-5">Category</div>
                <div className="col-span-7">Layer resolution · Sector → Company → Department</div>
              </div>
              {policy.rules.map((r) => (
                <div key={r.category} className="border-b border-subtle/60 px-4 py-3">
                  <div className="grid grid-cols-12 items-start gap-3">
                    <div className="col-span-5">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-white">{r.label}</div>
                        {r.custom && <Pill tone="gold"><Tag className="h-3 w-3" /> Custom</Pill>}
                      </div>
                      <div className="text-[11px] text-slate-500">{r.desc}</div>
                    </div>
                    <div className="col-span-7">
                      <LayerCascade r={r} saving={saving === r.category} />
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">
                      Set {scope}:
                    </span>
                    {ACTIONS.map((a) => {
                      const active = (scope === "company" ? r.company_override : r.department_override) === a;
                      const m = ACTION_META[a];
                      return (
                        <button
                          key={a}
                          data-testid={`set-${r.category}-${a}`}
                          onClick={() => setAction(r.category, active ? null : a)}
                          className="rounded-md border px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider transition-colors"
                          style={
                            active
                              ? { color: m.color, background: m.bg, borderColor: m.border }
                              : { color: "#64748B", borderColor: "#1E2A3C" }
                          }
                        >
                          {a}
                        </button>
                      );
                    })}
                    <label className="ml-auto flex cursor-pointer items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      <input
                        type="checkbox"
                        data-testid={`monitor-${r.category}`}
                        checked={!!r.monitor}
                        onChange={() => toggleMonitor(r)}
                        className="accent-mask"
                      />
                      <Eye className="h-3 w-3" /> Monitor
                    </label>
                  </div>

                  {r.sector_floor_enforced && (
                    <div className="mt-2 flex items-center gap-1.5 text-[11px] text-warn">
                      <Info className="h-3 w-3" /> Override was looser than the Sector Pack floor — clamped to {r.effective_action}.
                    </div>
                  )}
                </div>
              ))}
            </Card>
            <p className="mt-3 flex items-center gap-2 font-mono text-[11px] text-slate-500">
              <Layers className="h-3.5 w-3.5" /> Changes apply to the next Gateway Chat request immediately.
            </p>
          </div>

          {/* Allowlist + custom categories + legend */}
          <div className="space-y-6">
            <Card className="p-5" data-testid="custom-cat-panel">
              <div className="mb-3 flex items-center gap-2">
                <Tag className="h-4 w-4 text-gold" />
                <h3 className="font-display text-sm font-bold">Custom Categories</h3>
              </div>
              <p className="mb-4 text-[11px] text-slate-500">
                Define your own sensitive-data category with a regex and/or keywords. It joins the
                detection pipeline and the layered policy above.
              </p>

              <div className="mb-4 space-y-2">
                {customCats.length === 0 && (
                  <p className="text-xs text-slate-600">No custom categories yet.</p>
                )}
                {customCats.map((c) => (
                  <div key={c.id} data-testid={`custom-cat-${c.id}`} className="rounded-lg border border-subtle bg-elevated/50 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-white">{c.label}</span>
                      <div className="flex items-center gap-2">
                        <ActionBadge action={c.default_action} small />
                        <button data-testid={`del-custom-${c.id}`} onClick={() => delCustomCat(c.id)} className="text-slate-500 hover:text-block">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    {c.desc && <div className="mt-0.5 text-[11px] text-slate-500">{c.desc}</div>}
                    <div className="mt-1.5 flex flex-wrap gap-1.5 font-mono text-[10px] text-slate-500">
                      {c.pattern && <span className="rounded bg-base px-1.5 py-0.5">/{c.pattern}/</span>}
                      {(c.keywords || []).map((k) => (
                        <span key={k} className="rounded bg-base px-1.5 py-0.5">{k}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 border-t border-subtle pt-3">
                <input
                  data-testid="cc-label"
                  value={ccForm.label}
                  onChange={(e) => setCcForm({ ...ccForm, label: e.target.value })}
                  placeholder="Category name (e.g. Project Codenames)"
                  className="w-full rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm outline-none focus:border-brand"
                />
                <input
                  data-testid="cc-desc"
                  value={ccForm.desc}
                  onChange={(e) => setCcForm({ ...ccForm, desc: e.target.value })}
                  placeholder="Short description (optional)"
                  className="w-full rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm outline-none focus:border-brand"
                />
                <input
                  data-testid="cc-pattern"
                  value={ccForm.pattern}
                  onChange={(e) => { setCcForm({ ...ccForm, pattern: e.target.value }); setCcMatches(null); }}
                  placeholder="Regex pattern (optional), e.g. PRJ-\\d{4}"
                  className="w-full rounded-lg border border-subtle bg-elevated px-3 py-2 font-mono text-xs outline-none focus:border-brand"
                />
                <input
                  data-testid="cc-keywords"
                  value={ccForm.keywords}
                  onChange={(e) => { setCcForm({ ...ccForm, keywords: e.target.value }); setCcMatches(null); }}
                  placeholder="Keywords, comma-separated (optional)"
                  className="w-full rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm outline-none focus:border-brand"
                />
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">Default</span>
                  <select
                    data-testid="cc-action"
                    value={ccForm.default_action}
                    onChange={(e) => setCcForm({ ...ccForm, default_action: e.target.value })}
                    className="flex-1 rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm outline-none focus:border-brand"
                  >
                    {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                {ccErr && <p className="text-xs text-block">{ccErr}</p>}

                <div className="rounded-lg border border-dashed border-subtle bg-base/40 p-2.5">
                  <div className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    <Tag className="h-3 w-3" /> Test console — preview matches before saving
                  </div>
                  <textarea
                    data-testid="cc-test-sample"
                    value={ccSample}
                    onChange={(e) => setCcSample(e.target.value)}
                    rows={2}
                    placeholder="Paste sample text to test this pattern/keywords…"
                    className="w-full resize-none rounded-lg border border-subtle bg-elevated px-3 py-2 text-xs outline-none focus:border-brand"
                  />
                  <button
                    data-testid="cc-test-btn"
                    onClick={testPattern}
                    disabled={ccTestBusy || !ccSample.trim()}
                    className="mt-2 flex items-center gap-1.5 rounded-lg border border-subtle px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 transition-colors hover:border-brand/50 hover:text-brand disabled:opacity-40"
                  >
                    {ccTestBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />} Test pattern
                  </button>
                  {ccMatches && (
                    <div data-testid="cc-test-result" className="mt-2 text-xs">
                      {ccMatches.error ? (
                        <span className="text-block">{ccMatches.error}</span>
                      ) : ccMatches.match_count === 0 ? (
                        <span className="text-slate-500">No matches in the sample text.</span>
                      ) : (
                        <div>
                          <span className="text-allow">{ccMatches.match_count} match(es):</span>
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {ccMatches.matches.map((m, i) => (
                              <span key={i} className="rounded-md border border-mask/40 bg-mask/10 px-2 py-0.5 font-mono text-[11px] text-mask" title={m.source}>
                                {m.matched_text}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Btn data-testid="cc-add" onClick={addCustomCat} disabled={ccBusy} className="w-full">
                  {ccBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add custom category
                </Btn>
              </div>
            </Card>

            <Card className="p-5">
              <div className="mb-3 flex items-center gap-2">
                <Server className="h-4 w-4 text-brand" />
                <h3 className="font-display text-sm font-bold">Provider Allowlist ({scope})</h3>
              </div>
              <p className="mb-4 text-[11px] text-slate-500">
                US providers are never available for unresolved sensitive content, regardless of this setting.
              </p>
              <div className="space-y-2">
                {providers.map((p) => {
                  const on = (currentAllow || []).includes(p.id);
                  return (
                    <label
                      key={p.id}
                      data-testid={`allow-${p.id}`}
                      className="flex cursor-pointer items-center gap-3 rounded-lg border border-subtle bg-elevated/50 px-3 py-2.5"
                    >
                      <input type="checkbox" checked={on} onChange={() => toggleProvider(p.id)} className="accent-brand" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-white">{p.name}</div>
                        <div className="font-mono text-[10px] text-slate-500">{p.jurisdiction}</div>
                      </div>
                      {p.eu_sovereign ? <Pill tone="eu">EU</Pill> : <Pill tone="muted">US</Pill>}
                    </label>
                  );
                })}
              </div>
            </Card>

            <Card className="p-5">
              <div className="mb-3 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-brand" />
                <h3 className="font-display text-sm font-bold">Action legend</h3>
              </div>
              <div className="space-y-2">
                {ACTIONS.map((a) => (
                  <div key={a} className="flex items-center gap-3">
                    <ActionBadge action={a} small />
                    <span className="text-xs text-slate-400">
                      {a === "ALLOW" && "Passes through untouched."}
                      {a === "WARN" && "Proceeds, flagged & logged."}
                      {a === "MASK" && "Sensitive tokens redacted first."}
                      {a === "BLOCK" && "Never leaves the gateway."}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}
    </Section>
  );
}

function PolicyDiff({ rules, scope }) {
  const diffs = (rules || [])
    .filter((r) => r.effective_action !== r.sector_default)
    .map((r) => ({
      ...r,
      direction: SEVERITY[r.effective_action] > SEVERITY[r.sector_default] ? "stricter" : "looser",
      by: r.department_override && r.department_override !== r.company_override
        ? "Department"
        : r.company_override
        ? "Company"
        : r.sector_floor_enforced
        ? "Sector floor"
        : "—",
    }));

  return (
    <Card className="mb-4 p-4" data-testid="policy-diff">
      <div className="mb-2 flex items-center gap-2">
        <Layers className="h-4 w-4 text-brand" />
        <h3 className="font-display text-sm font-bold">Changes from the Sector Pack floor</h3>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-slate-500">
          {diffs.length} of {rules?.length || 0} categories
        </span>
      </div>
      {diffs.length === 0 ? (
        <p data-testid="policy-diff-empty" className="text-[11px] text-slate-500">
          This {scope === "department" ? "department overlay" : "company profile"} matches the Sector Pack
          floor exactly — no overrides in effect.
        </p>
      ) : (
        <div className="space-y-1.5" data-testid="policy-diff-list">
          {diffs.map((r) => (
            <div key={r.category} data-testid={`policy-diff-${r.category}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-subtle bg-elevated/50 px-3 py-2">
              <span className="text-xs font-semibold text-white">{r.label}</span>
              {r.custom && <Pill tone="gold"><Tag className="h-3 w-3" /> Custom</Pill>}
              <span className="ml-auto flex flex-wrap items-center gap-1.5">
                <LayerChip label="S" action={r.sector_default} />
                <span className="text-slate-700">→</span>
                <LayerChip label="C" action={r.company_override} inherited={!r.company_override} />
                <span className="text-slate-700">→</span>
                <LayerChip label="D" action={r.department_override} inherited={!r.department_override} />
                <span className="text-slate-600">⇒</span>
                <ActionBadge action={r.effective_action} small />
              </span>
              <span
                className="rounded px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider"
                style={{ color: r.direction === "stricter" ? "#F59E0B" : "#06B6D4", background: r.direction === "stricter" ? "rgba(245,158,11,0.12)" : "rgba(6,182,212,0.12)" }}
              >
                {r.direction} · by {r.by}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

